import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, FileCheck, Loader2, AlertCircle, Sparkles, RotateCcw, MessageCircle } from "lucide-react";
import { api, waUrl, WA_MESSAGES } from "../lib/api";
import { toast } from "sonner";

const INTAKE_OPTIONS = ["September 2026", "Januari 2027", "September 2027", "Belum yakin"];

export default function DocumentScreener() {
    const [file, setFile] = useState(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const [target, setTarget] = useState("S1");
    const [country, setCountry] = useState("Belum yakin");
    const [intake, setIntake] = useState("September 2026");
    const [major, setMajor] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const handleFile = (f) => {
        if (!f) return;
        if (f.size > 10 * 1024 * 1024) {
            toast.error("File maksimal 10 MB ya, Kak");
            return;
        }
        const ext = f.name.split(".").pop().toLowerCase();
        if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
            toast.error("Format harus PDF, JPG, atau PNG");
            return;
        }
        setFile(f);
    };

    const submit = async () => {
        if (!file) {
            toast.error("Unggah dokumen dulu ya, Kak");
            return;
        }
        if (!name) {
            toast.error("Isi nama dulu ya, Kak");
            return;
        }
        if (!email && !whatsapp) {
            toast.error("Isi email atau WhatsApp dulu agar kami bisa hubungi Kakak");
            return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", name);
            if (email) fd.append("email", email);
            if (whatsapp) fd.append("whatsapp", whatsapp);
            fd.append("target_degree", target);
            if (major) fd.append("major", `${major} · Tujuan: ${country} · Intake: ${intake}`);
            else fd.append("major", `Tujuan: ${country} · Intake: ${intake}`);
            const r = await api.post("/ai/screen-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(r.data);
        } catch (e) {
            toast.error("Maaf Kak, AI gagal memproses. Coba lagi ya.");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setFile(null);
        setResult(null);
    };

    const skorColor = (s) => {
        if (s >= 80) return { ring: "#10b981", text: "text-green-600", label: "Peluang Sangat Tinggi!" };
        if (s >= 60) return { ring: "#F5A623", text: "text-[#F5A623]", label: "Peluang Tinggi" };
        return { ring: "#ef4444", text: "text-red-500", label: "Perlu Persiapan Lebih" };
    };

    return (
        <section id="screener" className="bg-[#F8FAFC] py-20 md:py-28" data-testid="screener-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A1A2F] text-white text-xs font-semibold mb-4">
                        <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
                        LANGKAH 3 · CEK KESIAPAN
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1A2F] tracking-tight leading-tight">
                        Cek Kesiapan Dokumen Kuliah Luar Negeri
                    </h2>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed">
                        Upload transkrip, sertifikat IELTS/TOEFL, CV, atau dokumen pendukung. Sistem kami akan membantu membaca kesiapan awal, lalu <strong className="text-[#0A1A2F]">Counselor Senior</strong> akan meninjau rekomendasinya.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-8 md:p-10">
                        {!result ? (
                            <>
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setDragOver(false);
                                        handleFile(e.dataTransfer.files?.[0]);
                                    }}
                                    onClick={() => inputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${dragOver ? "border-[#F5A623] bg-[#F5A623]/10" : "border-slate-300 hover:border-[#0A1A2F]"}`}
                                    data-testid="upload-dropzone"
                                >
                                    <input
                                        ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => handleFile(e.target.files?.[0])}
                                        className="hidden"
                                        data-testid="file-input"
                                    />
                                    {file ? (
                                        <div className="flex items-center justify-center gap-3 text-[#0A1A2F]">
                                            <FileCheck className="w-6 h-6 text-green-600" />
                                            <div className="text-left">
                                                <div className="font-semibold text-sm">{file.name}</div>
                                                <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <Upload className="w-10 h-10 mx-auto text-slate-400" />
                                            <p className="mt-3 font-semibold text-[#0A1A2F]">Klik atau seret file ke sini</p>
                                            <p className="text-xs text-slate-500 mt-1">PDF, JPG, atau PNG · Maks 10 MB</p>
                                        </>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-3 mt-5">
                                    <Field label="Nama Lengkap *" value={name} onChange={setName} tid="screen-name" />
                                    <Field label="Email" value={email} onChange={setEmail} type="email" tid="screen-email" />
                                    <Field label="WhatsApp *" value={whatsapp} onChange={setWhatsapp} tid="screen-whatsapp" />
                                    <Field label="Jurusan minat (opsional)" value={major} onChange={setMajor} tid="screen-major" />
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Target Gelar</label>
                                        <select
                                            value={target} onChange={(e) => setTarget(e.target.value)}
                                            data-testid="screen-target"
                                            className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 focus:border-[#0A1A2F] outline-none text-sm bg-white"
                                        >
                                            <option>S1</option>
                                            <option>S2</option>
                                            <option>S3</option>
                                            <option>Foundation</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Negara Tujuan</label>
                                        <select
                                            value={country} onChange={(e) => setCountry(e.target.value)}
                                            data-testid="screen-country"
                                            className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 focus:border-[#0A1A2F] outline-none text-sm bg-white"
                                        >
                                            <option>Belum yakin</option>
                                            <option>Malaysia</option>
                                            <option>Australia</option>
                                            <option>UK</option>
                                            <option>Jerman</option>
                                            <option>Belanda</option>
                                            <option>Singapore</option>
                                            <option>Taiwan</option>
                                            <option>Korea Selatan</option>
                                            <option>USA</option>
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Target Intake</label>
                                        <select
                                            value={intake} onChange={(e) => setIntake(e.target.value)}
                                            data-testid="screen-intake"
                                            className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 focus:border-[#0A1A2F] outline-none text-sm bg-white"
                                        >
                                            {INTAKE_OPTIONS.map((i) => <option key={i}>{i}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <button
                                    onClick={submit} disabled={loading || !file}
                                    data-cta="cta_document_submit"
                                    data-testid="cta-document-submit"
                                    className="mt-6 w-full bg-[#F5A623] text-[#0A1A2F] hover:bg-[#D98F1B] rounded-full py-3.5 font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Menganalisa...</> : "Cek Dokumen Saya"}
                                </button>
                                <p className="mt-3 text-[11px] text-slate-500 text-center leading-relaxed">
                                    Dokumenmu hanya digunakan untuk analisis awal dan tidak akan dibagikan ke pihak lain tanpa persetujuan.
                                </p>
                            </>
                        ) : (
                            <ScoreCircle result={result} reset={reset} />
                        )}
                    </div>

                    <div className="bg-[#0A1A2F] p-8 md:p-10 text-white flex flex-col justify-between">
                        <div>
                            <h3 className="font-display font-semibold text-2xl mb-3">Apa yang Kami Cek?</h3>
                            <ul className="space-y-3 text-sm text-slate-300">
                                <Bullet>Kesesuaian nilai/IPK dengan target kampus</Bullet>
                                <Bullet>Skor bahasa Inggris (IELTS / TOEFL)</Bullet>
                                <Bullet>Kelengkapan dokumen</Bullet>
                                <Bullet>Potensi gap aplikasi</Bullet>
                                <Bullet>Rekomendasi langkah berikutnya</Bullet>
                            </ul>
                        </div>
                        <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-slate-300 leading-relaxed">
                                    Analisis ini bersifat <strong className="text-white">estimasi awal</strong>. Validasi final dilakukan oleh <strong className="text-white">Counselor Senior Gantari Edu</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ScoreCircle({ result, reset }) {
    const s = Math.max(1, Math.min(100, result?.skor || 50));
    const getColor = (v) => {
        if (v >= 80) return "#10b981";
        if (v >= 60) return "#F5A623";
        return "#ef4444";
    };
    const getLabel = (v) => {
        if (v >= 80) return "Peluang Sangat Tinggi!";
        if (v >= 60) return "Peluang Tinggi";
        return "Perlu Persiapan Lebih";
    };
    const c = getColor(s);
    const label = getLabel(s);
    const circ = 2 * Math.PI * 70;
    const offset = circ - (s / 100) * circ;

    return (
        <div className="text-center" data-testid="score-result">
            <div className="relative w-48 h-48 mx-auto">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
                    <circle cx="80" cy="80" r="70" stroke="#E2E8F0" strokeWidth="12" fill="none" />
                    <motion.circle
                        cx="80" cy="80" r="70" stroke={c} strokeWidth="12" fill="none"
                        strokeLinecap="round" strokeDasharray={circ}
                        initial={{ strokeDashoffset: circ }}
                        animate={{ strokeDashoffset: offset }}
                        transition={{ duration: 1.4, ease: "easeOut" }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-display font-bold text-5xl text-[#0A1A2F]">{s}</div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">dari 100</div>
                </div>
            </div>
            <div className="font-display font-bold text-lg mt-4" style={{ color: c }} data-testid="score-label">{label}</div>
            {result.ringkasan && <p className="text-sm text-slate-600 mt-3" data-testid="score-summary">{result.ringkasan}</p>}

            <div className="grid grid-cols-2 gap-3 mt-5 text-left">
                <Stat l="IPK Terdeteksi" v={result.ipk_terdeteksi || "—"} />
                <Stat l={result.jenis_bahasa || "Skor Bahasa"} v={result.skor_bahasa_terdeteksi || "—"} />
            </div>

            {result.rekomendasi_aksi?.length > 0 && (
                <div className="mt-5 text-left bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-2xl p-4">
                    <div className="text-xs font-bold uppercase tracking-widest text-[#F5A623] mb-2">Rekomendasi Aksi</div>
                    <ul className="text-sm text-[#0A1A2F] space-y-1.5 list-disc list-inside">
                        {result.rekomendasi_aksi.map((a, i) => <li key={`${a}-${i}`}>{a}</li>)}
                    </ul>
                </div>
            )}

            <a
                href={waUrl(WA_MESSAGES.document)}
                target="_blank"
                rel="noopener noreferrer"
                data-cta="cta_document_whatsapp"
                data-testid="document-cta-whatsapp"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB454] text-white rounded-full py-3 font-bold text-sm transition-all"
            >
                <MessageCircle className="w-4 h-4" /> Diskusi Hasil dengan Counselor Senior
            </a>

            <button onClick={reset} className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A1A2F]" data-testid="screen-reset-btn">
                <RotateCcw className="w-4 h-4" /> Coba dokumen lain
            </button>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#F5A623] mt-1.5 flex-shrink-0" />
            {children}
        </li>
    );
}

function Stat({ l, v }) {
    return (
        <div className="bg-slate-50 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
            <div className="font-bold text-sm text-[#0A1A2F] mt-1">{v}</div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", tid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</label>
            <input
                type={type} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={tid}
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 focus:border-[#0A1A2F] outline-none text-sm"
            />
        </div>
    );
}
